import * as THREE from 'three';
import {computeWorldBoundsExcludingAnimatedDoorParts} from 'components/customer/RoomPlanner/helpers/calculations';
import {DEFAULT_POSITION_Y} from 'components/customer/RoomPlanner/helpers/renderCabinetGroup';

/**
 * Floor plane Y matching how floor cabinets / placeholders are seated:
 * `-(wallHeightScene / 2) + DEFAULT_POSITION_Y.sitOnFloor`.
 * @param {number} wallHeightScene - Wall height already in scene units
 * @return {number} World Y of the floor seating plane
 */
export const getSitOnFloorY = (wallHeightScene: number): number =>
    -(wallHeightScene / 2) + DEFAULT_POSITION_Y.sitOnFloor;

/**
 * World Y of the wall top. The wall mesh is shifted up by the same
 * sit-on-floor offset cabinets use, so the top is `wallHeight/2 + sitOnFloor`,
 * not `wallHeight/2`. Clamping a hanging appliance to `wallHeight/2` leaves
 * it about 250mm short of the ceiling.
 * @param {number} wallHeightScene - Wall height already in scene units
 * @return {number} World Y the mesh top may reach
 */
export const getWallTopY = (wallHeightScene: number): number =>
    wallHeightScene / 2 + DEFAULT_POSITION_Y.sitOnFloor;

/**
 * Distance from the floor origin cabinets use (`+ wallHalf - 50`) to the
 * mesh bottom, in scene units. Placeholders use the live AABB bottom (GLB
 * pivots are often not at the floor). Same -50 as cabinets so a hanging
 * upper placeholder labels 1550mm like a wall cabinet, not 1545.
 * @param {THREE.Group} group - Cabinet or placeholder
 * @param {number} wallHeightScene - Wall height in scene units
 * @return {number} Floor clearance in scene units (may be slightly negative)
 */
export const getFloorClearanceSceneUnits = (
    group: THREE.Group<THREE.Object3DEventMap>,
    wallHeightScene: number
): number => {
    if (group.userData?.isPlaceholder) {
        const box = computeWorldBoundsExcludingAnimatedDoorParts(group);
        if (box.isEmpty() || !Number.isFinite(box.min.y)) {
            return 0;
        }
        // Same -50 origin as cabinets so an upper placeholder at the default
        // wall-cabinet underside labels 1550, not 1545.
        return box.min.y + wallHeightScene / 2 - 50;
    }
    return group.position.y + wallHeightScene / 2 - 50;
};

/**
 * Floor clearance label in mm (same rounding as arrowGroupLeft-distance).
 * @param {THREE.Group} group - Cabinet or placeholder
 * @param {number} wallHeightScene - Wall height in scene units
 * @param {number} scale - mm→scene scale (typically 0.2)
 * @return {number} Non-negative floor clearance in mm
 */
export const getFloorClearanceMm = (
    group: THREE.Group<THREE.Object3DEventMap>,
    wallHeightScene: number,
    scale: number
): number => {
    const sceneClearance = getFloorClearanceSceneUnits(group, wallHeightScene);
    const safeScale = scale > 0 ? scale : 0.2;
    return Math.max(0, Math.round(sceneClearance / safeScale - 5));
};
